<!--
Schedule Maintenance Modal Form
- Upsert form for creating or editing preventative maintenance schedules.
- Two-panel collapsible widget layout consistent with Enterprise V2 Move & Reserve forms.
- Left Panel: General Information (Asset ComboBox with infinite scroll lazy loading, Maintenance Type with quick Add/Edit modal launch, Assigned Person).
- Right Panel: Timing & Recurrence (One-Time Mode with user timezone min-date validation, Recurring Mode with interval/frequency and start/end dates, Notes).
- Sub-dialogs: Asset selection grid lookup, Person selection grid lookup, Maintenance Type creation modal.
-->
@if (_active) {
  <kendo-window
    #mainWindow
    title="{{ _pageState == 2 ? 'ADD NEW RECORD' : _pageState == 3 ? 'EDIT RECORD' : _pageState == 1 ? 'VIEW RECORD' : 'Schedule Maintenance' }}"
    (close)="onCancel()"
    (stateChange)="_onStateChanges($event)"
    class="fullWidthFormForm"
    (keydown.enter)="$event.preventDefault()"
    >
    <form novalidate [formGroup]="form" (keydown.enter)="$event.preventDefault()" class="form_style">
      <!-- Top Action Header matching Reserve Popup Design -->
      <div class="row">
        <div class="col-12">
          <div class="reservation-section-header">
            <div class="reservation-section-header-title">
              <span>Schedule Maintenance</span>
            </div>
            <div class="header-actions">
              <!-- Close Button -->
              <button class="steps_buttons_class" kendoButton type="button" (click)="onCancel()">
                <span>
                  <img [src]="'/assets/menu/' + _theme + '/close-schedule-report-icon-green.svg'" />
                </span>
                Close
              </button>
              <!-- Save & Close Button -->
              <button
                class="steps_buttons_class"
                kendoButton
                type="button"
                (click)="_onSave('saveAndClose')"
                [disabled]="isSaving"
                >
                <span>
                  <img [src]="'/assets/menu/' + _theme + '/save-icon-group.svg'" />
                </span>
                Save & Close
              </button>
              <!-- Save Button -->
              <button
                class="steps_buttons_class"
                kendoButton
                type="button"
                (click)="_onSave('save')"
                [disabled]="isSaving"
                >
                <span>
                  <img [src]="'/assets/menu/' + _theme + '/save-icon-group.svg'" />
                </span>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Form of Transaction: Two-Panel Widget Layout -->
      <div class="row no-row-margin">
        <!-- Left Panel: Maintenance Information -->
        <div class="col-12 col-lg-6 small-pad-right">
          <div [ngClass]="isGeneralInformationExpanded ? 'transactionWidget' : 'transactionWidget-collapse'">
            <div class="row">
              <div class="col-12 sectionheader special_style_formgroup">
                <span class="titleStandardStyle">Step 1: Maintenance Information</span>
                <span
                  [ngClass]="isGeneralInformationExpanded ? 'k-icon k-i-sort-asc-sm' : 'k-icon k-i-sort-desc-sm'"
                  class="float-right pointer-click"
                  (click)="isGeneralInformationExpanded = !isGeneralInformationExpanded"
                ></span>
              </div>
              @if (isGeneralInformationExpanded) {
                <!-- Maintenance Number / Code -->
                <div class="col-12 margin-top-2">
                  <div class="form-group special_style_formgroup">
                    <div class="row align-items-center">
                      <div class="col-3 col-xl-2">
                        <label class="control-label">Maintenance No. *</label>
                      </div>
                      <div class="col-9 col-xl-10 transaction-move-input-fields">
                        <input
                          type="text"
                          class="k-textbox writebox bordered newFormControlWidth"
                          formControlName="maintenanceCode"
                          placeholder="Maintenance Number"
                          required
                          />
                          @if (!isEdit) {
                            <a
                              class="cursor-pointer asap-input-icon ms-2"
                              (click)="_generateBarcodeClick()"
                              title="Auto-generate Maintenance Number"
                              >
                              <img class="searchIcon" [src]="'/assets/menu/' + _theme + '/auto-generate-icon.svg'" />
                            </a>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Asset Number Selection -->
                  <div class="col-12 margin-top-2">
                    <div class="form-group special_style_formgroup">
                      <div class="row align-items-center">
                        <div class="col-3 col-xl-2">
                          <label class="control-label">Asset Number *</label>
                        </div>
                        <div class="col-9 col-xl-10 transaction-move-input-fields">
                          <kendo-combobox
                            #Kendo_Combobox_Assets
                            formControlName="assetId"
                            [data]="assetsFiltered"
                            [textField]="'assetNumber'"
                            [valueField]="'id'"
                            [valuePrimitive]="true"
                            [filterable]="true"
                            [suggest]="true"
                            (filterChange)="handleAssetFilter($event)"
                            (opened)="scrollCombobox($event)"
                            (close)="onComboboxClosed()"
                            placeholder="Choose an Asset"
                            class="bordered newFormControlWidth"
                            required
                            >
                            <ng-template kendoComboBoxNoDataTemplate>
                              <div class="stocksettEnabledContainer">
                                <span class="missing-item-txt">No data found.</span>
                              </div>
                            </ng-template>
                            <ng-template kendoComboBoxItemTemplate let-dataItem>
                              <div class="transactions-receive-stock-dropdown-item">
                                <div class="truncate">
                                  <strong>{{ dataItem.assetNumber }}</strong>
                                  @if (dataItem.assetName || dataItem.name) {
                                    <span> | {{ dataItem.assetName || dataItem.name }}</span>
                                  }
                                </div>
                              </div>
                            </ng-template>
                          </kendo-combobox>
                          <span class="asap-input-icon">
                            <img
                              class="addIcon"
                              (click)="_openAssetsList()"
                              [src]="'/assets/menu/' + _theme + '/multi-select-icon.svg'"
                              title="Select Asset from list"
                              />
                            </span>
                          </div>
                        </div>
                        @if (form.get('assetId')?.touched && form.get('assetId')?.errors?.['required']) {
                          <div
                            class="row"
                            >
                            <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                              Asset Number is required.
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <!-- Maintenance Type -->
                    <div class="col-12 margin-top-2">
                      <div class="form-group special_style_formgroup">
                        <div class="row align-items-center">
                          <div class="col-3 col-xl-2">
                            <label class="control-label">Maintenance Type *</label>
                          </div>
                          <div class="col-9 col-xl-10 transaction-move-input-fields d-flex align-items-center">
                            <kendo-combobox
                              #Kendo_Combobox_MaintenanceTypes
                              formControlName="maintenanceTypeId"
                              [data]="maintenanceTypesFiltered"
                              [textField]="'maintenanceTypeCode'"
                              [valueField]="'id'"
                              [valuePrimitive]="true"
                              [suggest]="true"
                              [filterable]="true"
                              (filterChange)="handleMaintenanceTypeFilter($event)"
                              placeholder="Choose a Maintenance Type"
                              class="bordered newFormControlWidth"
                              required
                              >
                              <ng-template kendoComboBoxNoDataTemplate>
                                <div class="stocksettEnabledContainer">
                                  <span class="missing-item-txt">No data found.</span>
                                </div>
                              </ng-template>
                              <ng-template kendoComboBoxItemTemplate let-dataItem>
                                <div class="transactions-receive-stock-dropdown-item">
                                  <div class="truncate">
                                    <strong>{{ dataItem.maintenanceTypeCode }}</strong>
                                    @if (dataItem.maintenanceTypeName) {
                                      <span> | {{ dataItem.maintenanceTypeName }}</span>
                                    }
                                  </div>
                                </div>
                              </ng-template>
                            </kendo-combobox>
                            <a
                              class="cursor-pointer asap-input-icon ms-2"
                              (click)="openAddType()"
                              title="Add New Maintenance Type">
                              <img class="searchIcon" [src]="'/assets/menu/' + _theme + '/add-new-icon_grid.svg'" />
                            </a>
                          </div>
                        </div>
                        @if (form.get('maintenanceTypeId')?.touched && form.get('maintenanceTypeId')?.errors?.['required']) {
                          <div
                            class="row"
                            >
                            <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                              Maintenance Type is required.
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <!-- Assigned To -->
                    <div class="col-12 margin-top-2">
                      <div class="form-group special_style_formgroup">
                        <div class="row">
                          <div class="col-3 col-xl-2">
                            <label class="control-label">Assigned to</label>
                          </div>
                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                            <kendo-combobox
                              #Kendo_Combobox_Persons
                              formControlName="personId"
                              [data]="personsFiltered"
                              [textField]="'displayName'"
                              [valueField]="'id'"
                              [valuePrimitive]="true"
                              [suggest]="true"
                              [filterable]="true"
                              (filterChange)="handlePersonFilter($event)"
                              placeholder="Choose a Person"
                              class="bordered newFormControlWidth"
                              >
                              <ng-template kendoComboBoxNoDataTemplate>
                                <div class="stocksettEnabledContainer">
                                  <span class="missing-item-txt">No data found.</span>
                                </div>
                              </ng-template>
                              <ng-template kendoComboBoxItemTemplate let-dataItem>
                                <div class="transactions-receive-stock-dropdown-item">
                                  <div class="truncate">
                                    <strong>{{ dataItem.code || dataItem.fullName }}</strong>
                                    @if (dataItem.firstName || dataItem.lastName) {
                                      <span>
                                        ({{ dataItem.firstName }} {{ dataItem.lastName }})
                                      </span>
                                    }
                                  </div>
                                </div>
                              </ng-template>
                            </kendo-combobox>
                            <span class="asap-input-icon">
                              <img
                                class="addIcon"
                                (click)="_openPersonsList()"
                                [src]="'/assets/menu/' + _theme + '/multi-select-icon.svg'"
                                title="Select Person from list"
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
              <!-- Right Panel: Step 2: One-Time / Recurring Maintenance -->
              <div class="col-12 col-lg-6 small-pad-left">
                <div [ngClass]="isTimingExpanded ? 'transactionWidget' : 'transactionWidget-collapse'">
                  <div class="row">
                    <div class="col-12 sectionheader special_style_formgroup">
                      <span class="titleStandardStyle">{{ form.get('isRecurring')?.value ? 'Step 2: Recurring Maintenance' : 'Step 2: One-Time Maintenance' }}</span>
                      <span
                        [ngClass]="isTimingExpanded ? 'k-icon k-i-sort-asc-sm' : 'k-icon k-i-sort-desc-sm'"
                        class="float-right pointer-click"
                        (click)="isTimingExpanded = !isTimingExpanded"
                      ></span>
                    </div>
                    @if (isTimingExpanded) {
                      <!-- Mode: One Time vs Recurring -->
                      <div class="col-12 margin-top-2">
                        <div class="form-group special_style_formgroup">
                          <div class="row">
                            <div class="col-3 col-xl-2">
                              <label class="control-label">Mode</label>
                            </div>
                            <div class="col-9 col-xl-10">
                              <div class="transaction-move-grid-top-actions">
                                <div class="transaction-move-grid-top-actions-radio">
                                  <input
                                    type="radio"
                                    id="schedule-mode-onetime"
                                    name="isRecurring"
                                    [value]="false"
                                    formControlName="isRecurring"
                                    kendoRadioButton
                                    />
                                    <label class="k-radio-label ms-1" for="schedule-mode-onetime">One Time</label>
                                  </div>
                                  <div class="transaction-move-grid-top-actions-radio">
                                    <input
                                      type="radio"
                                      id="schedule-mode-recurring"
                                      name="isRecurring"
                                      [value]="true"
                                      formControlName="isRecurring"
                                      kendoRadioButton
                                      />
                                      <label class="k-radio-label ms-1" for="schedule-mode-recurring">Recurring</label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <!-- Sub-Mode: Date based vs Asset data field based -->
                          <div class="col-12 margin-top-2">
                            <div class="form-group special_style_formgroup">
                              <div class="row">
                                <div class="col-3 col-xl-2">
                                  <label class="control-label">Interval Type</label>
                                </div>
                                <div class="col-9 col-xl-10">
                                  <div class="transaction-move-grid-top-actions">
                                    <div class="transaction-move-grid-top-actions-radio">
                                      <input
                                        type="radio"
                                        id="interval-type-date"
                                        name="timeInterval"
                                        [value]="true"
                                        formControlName="timeInterval"
                                        kendoRadioButton
                                        />
                                        <label class="k-radio-label ms-1" for="interval-type-date">Date based</label>
                                      </div>
                                      <div class="transaction-move-grid-top-actions-radio">
                                        <input
                                          type="radio"
                                          id="interval-type-asset"
                                          name="timeInterval"
                                          [value]="false"
                                          formControlName="timeInterval"
                                          kendoRadioButton
                                          />
                                          <label class="k-radio-label ms-1" for="interval-type-asset">Asset data field based</label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <!-- ONE-TIME: Date-Based Fields -->
                              @if (!form.get('isRecurring')?.value && form.get('timeInterval')?.value !== false) {
                                <div class="col-12 margin-top-2">
                                  <div class="form-group special_style_formgroup">
                                    <div class="row align-items-center">
                                      <div class="col-3 col-xl-2">
                                        <label class="control-label">Scheduled For *</label>
                                      </div>
                                      <div class="col-9 col-xl-10 transaction-move-input-fields">
                                        <kendo-datetimepicker
                                          formControlName="scheduledFor"
                                          placeholder="Set Date and Time"
                                          class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                          [format]="_datePickerFormat"
                                          [min]="_minDateToday"
                                          required
                                        ></kendo-datetimepicker>
                                      </div>
                                    </div>
                                    @if (form.get('scheduledFor')?.touched && form.get('scheduledFor')?.errors?.['required']) {
                                      <div
                                        class="row"
                                        >
                                        <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                          Scheduled For is required.
                                        </div>
                                      </div>
                                    }
                                    @if (form.get('scheduledFor')?.touched && form.get('scheduledFor')?.errors?.['min']) {
                                      <div
                                        class="row"
                                        >
                                        <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                          Scheduled date cannot be earlier than today.
                                        </div>
                                      </div>
                                    }
                                  </div>
                                </div>
                              }
                              <!-- ONE-TIME: Asset Data Field Based Fields -->
                              @if (!form.get('isRecurring')?.value && form.get('timeInterval')?.value === false) {
                                <div class="col-12 margin-top-2">
                                  <div class="form-group special_style_formgroup">
                                    <div class="row align-items-center">
                                      <div class="col-3 col-xl-2">
                                        <label class="control-label">Scheduled For *</label>
                                      </div>
                                      <div class="col-9 col-xl-10 transaction-move-input-fields d-flex align-items-center gap-2">
                                        <kendo-numerictextbox
                                          formControlName="scheduledForOtherInterval"
                                          [min]="0"
                                          [format]="'n2'"
                                          [autoCorrect]="true"
                                          placeholder="0.00"
                                          class="bordered"
                                          style="width: 100px !important; height: 28px !important;"
                                        ></kendo-numerictextbox>
                                        <kendo-combobox
                                          formControlName="maintenanceIntervalId"
                                          [data]="intervalsFiltered"
                                          [textField]="'name'"
                                          [valueField]="'id'"
                                          [valuePrimitive]="true"
                                          [suggest]="true"
                                          [filterable]="true"
                                          (filterChange)="handleIntervalFilter($event)"
                                          placeholder="Select Interval"
                                          class="bordered"
                                          style="width: calc(100% - 110px) !important; height: 28px !important;"
                                          required
                                          >
                                          <ng-template kendoComboBoxNoDataTemplate>
                                            <div class="stocksettEnabledContainer">
                                              <span class="missing-item-txt">No data found.</span>
                                            </div>
                                          </ng-template>
                                          <ng-template kendoComboBoxItemTemplate let-dataItem>
                                            <div class="transactions-receive-stock-dropdown-item">
                                              <div class="truncate">
                                                <strong>{{ dataItem.name }}</strong>
                                              </div>
                                            </div>
                                          </ng-template>
                                        </kendo-combobox>
                                      </div>
                                    </div>
                                    @if (form.get('scheduledForOtherInterval')?.touched && form.get('scheduledForOtherInterval')?.errors?.['required']) {
                                      <div
                                        class="row"
                                        >
                                        <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                          Scheduled value is required.
                                        </div>
                                      </div>
                                    }
                                    @if (form.get('maintenanceIntervalId')?.touched && form.get('maintenanceIntervalId')?.errors?.['required']) {
                                      <div
                                        class="row"
                                        >
                                        <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                          Interval field is required.
                                        </div>
                                      </div>
                                    }
                                  </div>
                                </div>
                              }
                              <!-- RECURRING: Date-Based Fields -->
                              @if (form.get('isRecurring')?.value && form.get('timeInterval')?.value !== false) {
                                <!-- Repeat Every -->
                                <div class="col-12 margin-top-2">
                                  <div class="form-group special_style_formgroup">
                                    <div class="row">
                                      <div class="col-3 col-xl-2">
                                        <label class="control-label">Repeat every *</label>
                                      </div>
                                      <div class="col-9 col-xl-10 transaction-move-input-fields d-flex align-items-center gap-2">
                                        <kendo-numerictextbox
                                          formControlName="frequency"
                                          [min]="1"
                                          [max]="999"
                                          [format]="'n0'"
                                          [autoCorrect]="true"
                                          class="bordered"
                                          style="width: 80px !important; height: 28px !important;"
                                        ></kendo-numerictextbox>
                                        <kendo-combobox
                                          #Kendo_Combobox_IntervalTypes
                                          formControlName="maintIntervalType"
                                          [data]="intervalTypesFiltered"
                                          [textField]="'name'"
                                          [valueField]="'id'"
                                          [valuePrimitive]="true"
                                          [suggest]="true"
                                          [filterable]="true"
                                          (filterChange)="handleIntervalTypeFilter($event)"
                                          placeholder="Select Interval"
                                          class="bordered"
                                          style="width: calc(100% - 90px) !important; height: 28px !important;"
                                          required
                                          >
                                          <ng-template kendoComboBoxNoDataTemplate>
                                            <div class="stocksettEnabledContainer">
                                              <span class="missing-item-txt">No data found.</span>
                                            </div>
                                          </ng-template>
                                          <ng-template kendoComboBoxItemTemplate let-dataItem>
                                            <div class="transactions-receive-stock-dropdown-item">
                                              <div class="truncate">
                                                <strong>{{ dataItem.name }}</strong>
                                              </div>
                                            </div>
                                          </ng-template>
                                        </kendo-combobox>
                                      </div>
                                    </div>
                                    @if (form.get('maintIntervalType')?.touched && form.get('maintIntervalType')?.errors?.['required']) {
                                      <div
                                        class="row"
                                        >
                                        <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                          Interval is required.
                                        </div>
                                      </div>
                                    }
                                  </div>
                                </div>
                                <!-- Recurrence Basis (Scheduled event vs Based on the performed event) -->
                                <div class="col-12 margin-top-2">
                                  <div class="form-group special_style_formgroup">
                                    <div class="row align-items-center">
                                      <div class="col-3 col-xl-2">
                                        <label class="control-label">Scheduled based</label>
                                      </div>
                                      <div class="col-9 col-xl-10">
                                        <div class="transaction-move-grid-top-actions d-flex align-items-center gap-3">
                                          <div class="transaction-move-grid-top-actions-radio">
                                            <input
                                              type="radio"
                                              id="basis-scheduled"
                                              name="scheduledBased"
                                              [value]="true"
                                              formControlName="scheduledBased"
                                              kendoRadioButton
                                              />
                                              <label class="k-radio-label ms-1" for="basis-scheduled">Scheduled event</label>
                                            </div>
                                            <div class="transaction-move-grid-top-actions-radio">
                                              <input
                                                type="radio"
                                                id="basis-performed"
                                                name="scheduledBased"
                                                [value]="false"
                                                formControlName="scheduledBased"
                                                kendoRadioButton
                                                />
                                                <label class="k-radio-label ms-1" for="basis-performed">Based on the performed event</label>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Start Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Start Date *</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="startDate"
                                              placeholder="Start Date"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                              [min]="_minDateToday"
                                              required
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                        @if (form.get('startDate')?.touched && form.get('startDate')?.errors?.['required']) {
                                          <div
                                            class="row"
                                            >
                                            <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                              Start date is required.
                                            </div>
                                          </div>
                                        }
                                      </div>
                                    </div>
                                    <!-- End Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">End Date</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="endDate"
                                              placeholder="End Date (Optional)"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                              [min]="form.get('startDate')?.value || _minDateToday"
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Basis Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Basis Date</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="recurringBasis"
                                              placeholder="Basis Date"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Next Due Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Next Due Date</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="dueNext"
                                              placeholder="Next Due Date"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  }
                                  <!-- RECURRING: Asset Data Field Based Fields -->
                                  @if (form.get('isRecurring')?.value && form.get('timeInterval')?.value === false) {
                                    <!-- Repeat Every -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Repeat Every *</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields d-flex align-items-center gap-2">
                                            <kendo-numerictextbox
                                              formControlName="frequency"
                                              [min]="1"
                                              [max]="999999"
                                              [format]="'n0'"
                                              [autoCorrect]="true"
                                              class="bordered"
                                              style="width: 80px !important; height: 28px !important;"
                                            ></kendo-numerictextbox>
                                            <kendo-combobox
                                              formControlName="maintenanceIntervalId"
                                              [data]="intervalsFiltered"
                                              [textField]="'name'"
                                              [valueField]="'id'"
                                              [valuePrimitive]="true"
                                              [suggest]="true"
                                              [filterable]="true"
                                              (filterChange)="handleIntervalFilter($event)"
                                              placeholder="Select Interval"
                                              class="bordered"
                                              style="width: calc(100% - 90px) !important; height: 28px !important;"
                                              required
                                              >
                                              <ng-template kendoComboBoxNoDataTemplate>
                                                <div class="stocksettEnabledContainer">
                                                  <span class="missing-item-txt">No data found.</span>
                                                </div>
                                              </ng-template>
                                              <ng-template kendoComboBoxItemTemplate let-dataItem>
                                                <div class="transactions-receive-stock-dropdown-item">
                                                  <div class="truncate">
                                                    <strong>{{ dataItem.name }}</strong>
                                                  </div>
                                                </div>
                                              </ng-template>
                                            </kendo-combobox>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Start Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Start Date</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="startDate"
                                              placeholder="Start Date"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- End Date -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">End Date</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-datepicker
                                              formControlName="endDate"
                                              placeholder="End Date (Optional)"
                                              class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                              [format]="_datePickerFormat"
                                            ></kendo-datepicker>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Starting Basis -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Starting Basis *</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-numerictextbox
                                              formControlName="recurringBasisOtherInterval"
                                              [min]="0"
                                              [format]="'n2'"
                                              [autoCorrect]="true"
                                              placeholder="0.00"
                                              class="bordered newFormControlWidth"
                                              style="height: 28px !important;"
                                            ></kendo-numerictextbox>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <!-- Next Due -->
                                    <div class="col-12 margin-top-2">
                                      <div class="form-group special_style_formgroup">
                                        <div class="row align-items-center">
                                          <div class="col-3 col-xl-2">
                                            <label class="control-label">Next Due</label>
                                          </div>
                                          <div class="col-9 col-xl-10 transaction-move-input-fields">
                                            <kendo-numerictextbox
                                              formControlName="dueNextOthers"
                                              [min]="0"
                                              [format]="'n2'"
                                              [autoCorrect]="true"
                                              placeholder="0.00"
                                              class="bordered newFormControlWidth"
                                              style="height: 28px !important;"
                                            ></kendo-numerictextbox>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  }
                                  <div class="section-separator"></div>
                                  <!-- Notes -->
                                  <div class="col-12 margin-top-2">
                                    <div class="form-group special_style_formgroup">
                                      <div class="row">
                                        <div class="col-3 col-xl-2">
                                          <label class="control-label">Notes</label>
                                        </div>
                                        <div class="col-9 col-xl-10 transaction-textarea-fields">
                                          <textarea
                                            formControlName="notes"
                                            rows="3"
                                            placeholder="Schedule notes and instructions..."
                                          ></textarea>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- TabStrip & Notes Sidebar Layout -->
                        <div class="example-wrapper">
                          <div class="row no_row_margin">
                            <div class="col-12 col-lg-9 col-xl-10 responsive_style numericboxtab" #kendo_tabstrip_wrapper>
                              <kendo-tabstrip (tabSelect)="_onTabSelect($event)" [tabPosition]="_kendoTabStripPosition">
                                <!-- Documents Tab -->
                                <kendo-tabstrip-tab #DocumentsTab [selected]="_selectedTab === 'Documents'">
                                  <ng-template kendoTabTitle>Documents</ng-template>
                                  <ng-template kendoTabContent>
                                    <asap-document-manager
                                      [(documents)]="_scheduleFilesDoc"
                                      [pageState]="_pageState"
                                      entityTitle="Schedule Maintenance"
                                    ></asap-document-manager>
                                  </ng-template>
                                </kendo-tabstrip-tab>
                                <!-- Stock Parts Tab -->
                                <kendo-tabstrip-tab #StockPartsTab [selected]="_selectedTab === 'StockParts'">
                                  <ng-template kendoTabTitle>
                                    <span>Stock Parts</span>
                                    @if (stockParts.length > 0) {
                                      <span class="stock-badge ms-1">({{ stockParts.length }})</span>
                                    }
                                  </ng-template>
                                  <ng-template kendoTabContent>
                                    <div class="stock-parts-wrapper">
                                      <div class="stock-parts-toolbar">
                                        <div class="toolbar-left d-flex align-items-center flex-wrap gap-2">
                                          <button
                                            type="button"
                                            class="asap-btn"
                                            kendoButton
                                            (click)="_openStockItemsList()"
                                            >
                                            <span>
                                              <img [src]="'/assets/menu/' + _theme + '/add-new-icon_grid.svg'" />
                                            </span>
                                            Add Stock Part
                                          </button>
                                          @if (form.get('isRecurring')?.value) {
                                            <div class="d-flex align-items-center gap-2 ms-3">
                                              <label class="control-label mb-0 small text-nowrap">Years to Forecast:</label>
                                              <kendo-numerictextbox
                                                formControlName="yearsToForcastStockQuantity"
                                                [min]="1"
                                                [max]="100"
                                                [format]="'n0'"
                                                [autoCorrect]="true"
                                                class="bordered"
                                                style="width: 70px !important; height: 26px !important;"
                                              ></kendo-numerictextbox>
                                            </div>
                                          }
                                          <span class="text-muted small ms-2">
                                            Replacement parts and required inventory items for this schedule.
                                          </span>
                                        </div>
                                        @if (stockParts.length > 0) {
                                          <div class="toolbar-right">
                                            <span>Parts: <strong>{{ stockParts.length }}</strong></span>
                                            <span>Total Estimated Cost: <strong class="text-success">{{ totalPartsCost | number: '1.2-2' }}</strong></span>
                                          </div>
                                        }
                                      </div>
                                      @if (stockParts.length > 0) {
                                        <div class="stock-parts-table-container">
                                          <table class="stock-parts-table">
                                            <thead>
                                              <tr>
                                                <th style="width: 16%;">Part #</th>
                                                <th style="width: 26%;">Description</th>
                                                <th style="width: 10%;">UOM</th>
                                                <th style="width: 13%;">Unit Cost</th>
                                                <th style="width: 13%;">Required Qty</th>
                                                <th style="width: 12%;">Extended Cost</th>
                                                @if (form.get('isRecurring')?.value) {
                                                  <th style="width: 10%;">Annual Forecast</th>
                                                }
                                                <th style="width: 5%; text-align: center;"></th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              @for (part of stockParts; track part; let i = $index) {
                                                <tr>
                                                  <td class="part-number">{{ part.stockNumber }}</td>
                                                  <td class="part-desc">{{ part.stockDescription || '-' }}</td>
                                                  <td><span class="part-uom">{{ part.unitOfMeasureCode || part.unitOfMeasureName || '-' }}</span></td>
                                                  <td>
                                                    <input
                                                      type="number"
                                                      class="part-input"
                                                      min="0"
                                                      step="0.01"
                                                      [(ngModel)]="part.cost"
                                                      [ngModelOptions]="{standalone: true}"
                                                      (ngModelChange)="onPartCostOrQtyChange(part)"
                                                      />
                                                    </td>
                                                    <td>
                                                      <input
                                                        type="number"
                                                        class="part-input"
                                                        min="0.0001"
                                                        step="1"
                                                        [(ngModel)]="part.requiredQuantity"
                                                        [ngModelOptions]="{standalone: true}"
                                                        (ngModelChange)="onPartCostOrQtyChange(part)"
                                                        />
                                                      </td>
                                                      <td class="part-extended-cost text-end">
                                                        {{ (part.extendedCost || 0) | number: '1.2-2' }}
                                                      </td>
                                                      @if (form.get('isRecurring')?.value) {
                                                        <td class="text-center">
                                                          <span class="forecast-badge" title="Annualized demand forecast based on recurrence interval">
                                                            {{ part.forecastedQuantity || calculateForecast(part) || 0 | number: '1.0-2' }}
                                                          </span>
                                                        </td>
                                                      }
                                                      <td class="text-center">
                                                        <span class="asap-grid-remove" (click)="removeStockPart(i)" title="Remove Part">
                                                          <span class="asap-grid-remove-icon">
                                                            <img [src]="'/assets/menu/' + _theme + '/delete-icon.svg'" />
                                                          </span>
                                                        </span>
                                                      </td>
                                                    </tr>
                                                  }
                                                </tbody>
                                              </table>
                                            </div>
                                          } @else {
                                            <div class="stock-parts-empty">
                                              <div class="empty-title">No stock parts associated with this schedule</div>
                                              <div class="empty-desc">
                                                Link required inventory items so replacement demand and costs can be forecasted automatically.
                                              </div>
                                              <button
                                                type="button"
                                                class="asap-btn"
                                                kendoButton
                                                (click)="_openStockItemsList()"
                                                >
                                                <span>
                                                  <img [src]="'/assets/menu/' + _theme + '/add-new-icon_grid.svg'" />
                                                </span>
                                                Select Stock Parts
                                              </button>
                                            </div>
                                          }
                                        </div>
                                      </ng-template>
                                    </kendo-tabstrip-tab>
                                  </kendo-tabstrip>
                                </div>
                                <!-- Notes Right Sidebar -->
                                <div class="col-12 col-lg-3 col-xl-2 responsive_style">
                                  <div class="borderedNotesContainer">
                                    <note
                                      #note
                                      [_pageState]="_pageState"
                                      [_model]="_model"
                                      [_page]="_page"
                                    ></note>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </form>
                          <!-- Asset Popup Selector (matching Reserve Popup addFromLibrary pattern) -->
                          @if (_assetsListEnabled) {
                            <kendo-window
                              title="Select Asset"
                              (close)="_closeAssetsList()"
                              [resizable]="false"
                              [minHeight]="120"
                              class="addFromLibrary"
                              >
                              <div class="greytabtop">
                                <button kendoButton class="topRightButtonSelect" (click)="_returnSelectedAssetsHandler($event)">
                                  Select & Return
                                </button>
                              </div>
                              <asap-grid-layout
                                [singleSelect]="true"
                                [selectable]="true"
                                [groupingEnabled]="true"
                                [pagingEnabled]="true"
                                [headerEnable]="true"
                                [headerTitle]="'Select An Asset'"
                                [entityName]="'asset'"
                                [addNewFormEnabled]="false"
                                [enableSiteSelection]="false"
                                [orderEnable]="true"
                                [sortingEnabled]="true"
                                [page]="'assets'"
                                [deletedEnabled]="false"
                                [selectByField]="'id'"
                                [selectByFieldTitle]="''"
                                [filteringEnable]="true"
                                [HideNotesFields]="true"
                                [resolver]="_getAssets"
                                (onRowSelectionChanges)="_onAssetsRowSelectionChanges($event)"
                                >
                                <ng-template pageListColumn [field]="'assetNumber'" [name]="'Asset #'" let-item>
                                  {{ item.assetNumber }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'name'" [name]="'Asset Name'" let-item>
                                  {{ item.name }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'site.code'" [name]="'Site'" let-item>
                                  {{ item.site?.code }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'location.code'" [name]="'Location'" let-item>
                                  {{ item.location?.code }}
                                </ng-template>
                              </asap-grid-layout>
                            </kendo-window>
                          }
                          <!-- Person Popup Selector (matching Reserve Popup addFromLibrary pattern) -->
                          @if (_personsListEnabled) {
                            <kendo-window
                              title="Select Person"
                              (close)="_closePersonsList()"
                              class="addFromLibrary"
                              >
                              <div class="greytabtop">
                                <button kendoButton class="topRightButtonSelect" (click)="_returnSelectedPersonsHandler($event)">
                                  Select & Return
                                </button>
                              </div>
                              <asap-grid-layout
                                [selectable]="true"
                                [singleSelect]="true"
                                [groupingEnabled]="true"
                                [pagingEnabled]="true"
                                [headerEnable]="true"
                                [headerTitle]="'Select Person'"
                                [entityName]="'person'"
                                [addNewFormEnabled]="false"
                                [orderEnable]="true"
                                [sortingEnabled]="true"
                                [page]="'persons'"
                                [deletedEnabled]="false"
                                [selectByField]="'id'"
                                [selectByFieldTitle]="'Select Person'"
                                [enableSiteSelection]="false"
                                [filteringEnable]="true"
                                [HideNotesFields]="true"
                                [resolver]="_getPersons"
                                (onRowSelectionChanges)="_onPersonsRowSelectionChanges($event)"
                                >
                                <ng-template pageListColumn [field]="'code'" [name]="'Code'" let-item>
                                  {{ item.code }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'firstName'" [name]="'First Name'" let-item>
                                  {{ item.firstName }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'lastName'" [name]="'Last Name'" let-item>
                                  {{ item.lastName }}
                                </ng-template>
                              </asap-grid-layout>
                            </kendo-window>
                          }
                          <!-- Stock Items Popup Selector (matching Reserve Popup addFromLibrary pattern) -->
                          @if (_stockItemsListEnabled) {
                            <kendo-window
                              title="Select Stock Items"
                              (close)="_closeStockItemsList()"
                              [resizable]="false"
                              [minHeight]="120"
                              class="addFromLibrary"
                              >
                              <div class="greytabtop">
                                <button kendoButton class="topRightButtonSelect" (click)="_returnSelectedStockItemsHandler($event)">
                                  Select & Return
                                </button>
                              </div>
                              <asap-grid-layout
                                [selectable]="true"
                                [singleSelect]="false"
                                [groupingEnabled]="true"
                                [pagingEnabled]="true"
                                [headerEnable]="true"
                                [headerTitle]="'Select Stock Items'"
                                [entityName]="'stockItem'"
                                [addNewFormEnabled]="false"
                                [enableSiteSelection]="false"
                                [orderEnable]="true"
                                [sortingEnabled]="true"
                                [page]="'stockItems'"
                                [deletedEnabled]="false"
                                [selectByField]="'id'"
                                [selectByFieldTitle]="'Select Stock Items'"
                                [filteringEnable]="true"
                                [HideNotesFields]="true"
                                [resolver]="_getStockItems"
                                (onRowSelectionChanges)="_onStockItemsRowSelectionChanges($event)"
                                >
                                <ng-template pageListColumn [field]="'number'" [name]="'Stock #'" let-item>
                                  {{ item.number }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'description'" [name]="'Description'" let-item>
                                  {{ item.description }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'inventoryUnitOfMeasure.code'" [name]="'UOM'" let-item>
                                  {{ item.inventoryUnitOfMeasure?.code || item.uomunitcode }}
                                </ng-template>
                                <ng-template pageListColumn [field]="'cost'" [name]="'Unit Cost'" let-item>
                                  {{ (item.costInfo?.cost || item.cost || 0) | number: '1.2-2' }}
                                </ng-template>
                              </asap-grid-layout>
                            </kendo-window>
                          }
                          @if (_assetsListEnabled || _personsListEnabled || _stockItemsListEnabled || _isDialiog) {
                            <div class="k-overlay"></div>
                          }
                          <!-- Maintenance Type Dialog (Enterprise V2 System Dialog) -->
                          @if (_isDialiog) {
                            <asap-dialog-layout
                              [componentName]="_dialogComponentName"
                              [data]="_dialogModel"
                              (closeDialog)="_closeDialog($event)"
                            ></asap-dialog-layout>
                          }
                        </kendo-window>
                      }
